import constants from './constants';
import { NON_ALPHANUMERIC_REGEX } from '../constRegex';

/** From Creatable Select { value, isNewValue }: sanitize only when isNewValue; else return value as-is. */
export const sanitizeRouteNameToAlphanumeric = (sel) => {
  const { value, isNewValue } = sel || {};
  if (value == null || value === '') return null;
  if (!isNewValue) return value;
  const sanitized = String(value).replace(NON_ALPHANUMERIC_REGEX, '').slice(0, constants.ROUTE_NAME_MAX_LENGTH);
  return sanitized === '' ? null : sanitized;
};

// Returns the route name value for the dropdown (must match option.value for selection).
// routeList uses last-N as value. Full name only for user-created routes (Creatable).
const getRouteNameDisplayValue = ({
  routeName,
  enableNewRouteAssignmentForTrailer,
  suffixLength = constants.ROUTE_NAME_SUFFIX_LENGTH,
  routeOptions = [],
}) => {
  if (routeName == null || routeName === '' || routeName === constants.ROUTE_NAME_DEFAULT)
    return null;
  if (!enableNewRouteAssignmentForTrailer) {
    return routeName.slice(-suffixLength);
  }
  // User-created routes (Creatable) have option.value === full route name
  const isUserCreated = routeOptions.some((r) => r.value === routeName);
  if (isUserCreated) return routeName;
  // routeList and trailer routes use last-N
  return routeName.length <= suffixLength
    ? routeName
    : routeName.slice(-suffixLength);
};

// Ensures the trailer's route is in the route options. Adds it if not present (no duplicate).
const ensureTrailerRouteInOptions = ({
  routeOptions,
  trailerRouteName,
  suffixLength = constants.ROUTE_NAME_SUFFIX_LENGTH,
}) => {
  if (!trailerRouteName || trailerRouteName === constants.ROUTE_NAME_DEFAULT)
    return routeOptions;
  const trailerRouteValue = trailerRouteName?.slice(-suffixLength);
  const isDuplicate= routeOptions.some(
    (route) => route.value === trailerRouteValue
  );

  if (isDuplicate) return routeOptions;
  const newOption = {
    label: trailerRouteName,
    value: trailerRouteValue,
  };
  return [newOption, ...routeOptions];
}

/** Show "Add Route" when trailer is selected AND (has no route OR is closed). Disabled when no trailer is selected — user must pick a trailer first. */
export const shouldShowAddRouteOption = ({
  trailer,
  enableNewRouteAssignmentForTrailer,
  isClosedTrailer = false,
}) =>
  Boolean(enableNewRouteAssignmentForTrailer) && Boolean(trailer?.trailerNumber) && (!trailer?.routeName || isClosedTrailer);

// Merges session-level user-created routes into route options so they persist after switching to another route.
export const mergeUserCreatedRoutes = (routeOptions, userCreatedRoutes = []) => {
  if (!userCreatedRoutes?.length) return routeOptions;
  const existingValues = new Set(routeOptions.map((r) => r.value));
  const newRoutes = userCreatedRoutes.filter((r) => !existingValues.has(r.value));
  return newRoutes.length ? [...routeOptions, ...newRoutes] : routeOptions;
};

// Resolves base route options for dropdown. User-created (typed): show full list. Appended from API: show only that route.
export const resolveBaseRouteOptions = ({
  filteredRouteOptions,
  trailer,
  routeList,
  trailerList,
  suffixLength = constants.ROUTE_NAME_SUFFIX_LENGTH,
}) => {
  if (filteredRouteOptions?.length > 0) return filteredRouteOptions;
  if (!trailer?.routeName) return routeList;
  const selectedRouteSuffix = trailer.routeName?.slice(-suffixLength);
  const matchedTrailer = trailerList?.find((t) => t.value === trailer.trailerId);
  const originalRouteSuffix = matchedTrailer?.routeName ?? null;
  const isAppendedFromApi = originalRouteSuffix === selectedRouteSuffix;
  return isAppendedFromApi ? [] : routeList;
};

/** Route list plus route names from all trailers (for duplicate-check / full options). Single-pass filter. */
export const getRouteOptionsIncludingFromAllTrailers = ({
  routeList = [],
  allTrailers = [],
  suffixLength = constants.ROUTE_NAME_SUFFIX_LENGTH,
}) => {
  const existingValues = new Set(routeList.map((r) => r.value));
  const seenNames = new Set();
  const fromTrailers = allTrailers
    .map((t) => t.routeName)
    .filter((name) => {
      if (!name || name === constants.ROUTE_NAME_DEFAULT) return false;
      if (seenNames.has(name)) return false;
      seenNames.add(name);
      if (existingValues.has(name.slice(-suffixLength))) return false;
      return true;
    })
    .map((name) => ({ label: name, value: name.slice(-suffixLength) }));
  return fromTrailers.length ? [...routeList, ...fromTrailers] : routeList;
};

/** Full route list for duplicate-check: base (mapping + trailers) + user-created for this trailer. */
export const getAllRouteOptionsForDuplicateCheck = ({
  baseRouteOptions = [],
  userCreatedRoutes = [],
}) => {
  const existingValues = new Set(baseRouteOptions.map((r) => String(r?.value ?? '').trim()));
  const userCreated = userCreatedRoutes.filter(
    (r) => r?.value && !existingValues.has(String(r.value).trim())
  );
  return userCreated.length ? [...baseRouteOptions, ...userCreated] : baseRouteOptions;
};

/** Single call for Route dropdown allOptions: route list + all trailers + user-created for trailer. */
export const getRouteOptionsForDuplicateCheck = ({
  routeList = [],
  allTrailers = [],
  userCreatedRoutes = [],
  suffixLength = constants.ROUTE_NAME_SUFFIX_LENGTH,
}) =>
  getAllRouteOptionsForDuplicateCheck({
    baseRouteOptions: getRouteOptionsIncludingFromAllTrailers({
      routeList,
      allTrailers,
      suffixLength,
    }),
    userCreatedRoutes,
  });

// When changing trailer, returns the route name to use on the new trailer.
// Re-applies the current slot's user-created route if the new trailer also owns it; otherwise falls back to the new trailer's own route.
export const resolveRouteOnTrailerChange = ({
  currentTrailer,
  newTrailerData,
  userCreatedRoutesByTrailer,
  enableNewRouteAssignmentForTrailer,
}) => {
  if (!enableNewRouteAssignmentForTrailer || !currentTrailer?.routeName || !newTrailerData?.trailerId) {
    return newTrailerData?.routeName ?? null;
  }
  const currentRoutes = userCreatedRoutesByTrailer[currentTrailer.trailerId] || [];
  const newTrailerRoutes = userCreatedRoutesByTrailer[newTrailerData.trailerId] || [];
  const isUserCreatedRoute = currentRoutes.some((r) => r.value === currentTrailer.routeName);
  const newTrailerHasRoute = newTrailerRoutes.some((r) => r.value === currentTrailer.routeName);
  return (isUserCreatedRoute && newTrailerHasRoute) ? currentTrailer.routeName : (newTrailerData?.routeName ?? null);
};

export {
  getRouteNameDisplayValue,
  ensureTrailerRouteInOptions,
}